import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, Copy, RefreshCw, ExternalLink, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Shopify() {
  const { company } = useCompany();
  const [settings, setSettings] = useState(null);
  const [shopDomain, setShopDomain] = useState("");
  const [secret, setSecret] = useState("");
  const [taxRate, setTaxRate] = useState(8.1);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotated, setRotated] = useState(null);

  const load = useCallback(async () => {
    if (!company?._id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/shopify-settings`);
      setSettings(res.data);
      setShopDomain(res.data.shop_domain || "");
      setEnabled(!!res.data.enabled);
      setTaxRate(res.data.default_tax_rate ?? 8.1);
      setSecret("");
    } catch (e) {
      toast.error("Konnte Shopify-Einstellungen nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [company?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!company?._id) return;
    setSaving(true);
    try {
      const body = {
        enabled,
        shop_domain: shopDomain.trim() || null,
        default_tax_rate: parseFloat(taxRate),
      };
      if (secret) body.webhook_secret = secret;
      const res = await apiClient.put(`/companies/${company._id}/shopify-settings`, body);
      setSettings(res.data);
      setSecret("");
      toast.success("Shopify-Einstellungen gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    if (!company?._id) return;
    if (!window.confirm(
      "Neuen Webhook-Secret generieren? Du musst ihn sofort in Shopify aktualisieren — sonst werden alle Webhooks abgelehnt."
    )) return;
    try {
      const res = await apiClient.post(`/companies/${company._id}/shopify-settings/rotate-secret`);
      setRotated(res.data.webhook_secret);
      toast.success("Neuer Webhook-Secret generiert.");
      load();
    } catch (e) {
      toast.error("Rotate fehlgeschlagen.");
    }
  };

  const copyToClipboard = (text, label = "Kopiert") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const webhookUrl = settings?.webhook_url
    ? `${BACKEND_URL}${settings.webhook_url}`
    : `${BACKEND_URL}/api/webhooks/shopify`;

  if (loading) {
    return (
      <div className="p-6 text-slate-500 text-sm" data-testid="shopify-loading">
        Lade…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="shopify-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/settings"
          className="text-sm text-slate-500 hover:text-slate-900"
          data-testid="shopify-back-link"
        >
          ← Einstellungen
        </Link>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
          <ShoppingBag className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Shopify-Connector</h1>
          <p className="text-sm text-slate-500 mt-1">
            Empfange Bestellungen automatisch als Rechnungen in deiner Buchhaltung.
            HMAC-verifiziert, Multi-Shop-fähig, Replay-geschützt.
          </p>
        </div>
        <Badge
          className={
            settings?.enabled
              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
              : "bg-slate-100 text-slate-600 border-slate-200"
          }
          data-testid="shopify-status-badge"
        >
          {settings?.enabled ? "AKTIV" : "INAKTIV"}
        </Badge>
      </div>

      {/* Konfig-Karte */}
      <Card className="p-6 space-y-5 border border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Connector aktivieren</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Wenn aktiv und Shop-Domain + Secret gesetzt sind, akzeptiert der Endpunkt
              eingehende Webhooks.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="shopify-enabled-switch"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Shop-Domain</label>
          <Input
            type="text"
            placeholder="mein-shop.myshopify.com"
            value={shopDomain}
            onChange={(e) => setShopDomain(e.target.value)}
            data-testid="shopify-shop-domain-input"
          />
          <p className="text-xs text-slate-500">
            Genau wie der Wert in <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">X-Shopify-Shop-Domain</code>.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            Webhook-Secret
            {settings?.has_secret && (
              <Badge variant="outline" className="text-[11px]">
                Aktuell: {settings.secret_preview}
              </Badge>
            )}
          </label>
          <Input
            type="password"
            placeholder={settings?.has_secret ? "(leer lassen, um aktuellen zu behalten)" : "Aus Shopify → Notifications kopieren"}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="shopify-secret-input"
          />
          <div className="flex items-center justify-between text-xs">
            <p className="text-slate-500">
              Manuell aus Shopify übernehmen <strong>ODER</strong> hier rotieren lassen:
            </p>
            <button
              type="button"
              onClick={handleRotate}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
              data-testid="shopify-rotate-secret-btn"
            >
              <RefreshCw className="w-3 h-3" />
              Neuen Secret generieren
            </button>
          </div>
        </div>

        <div className="space-y-2 max-w-xs">
          <label className="text-sm font-medium text-slate-700">
            Default-MWST (%)
          </label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            data-testid="shopify-tax-rate-input"
          />
          <p className="text-xs text-slate-500">
            Wird genutzt wenn die Order kein <code className="font-mono">total_tax</code> liefert.
          </p>
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button onClick={load} variant="outline" disabled={saving} data-testid="shopify-reset-btn">
            Verwerfen
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="shopify-save-btn">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Card>

      {/* Rotated Secret Banner */}
      {rotated && (
        <Card className="p-4 border-2 border-amber-300 bg-amber-50" data-testid="shopify-rotated-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-900 text-sm">
                Neuer Webhook-Secret — wird nur jetzt angezeigt!
              </div>
              <div className="mt-2 flex items-center gap-2">
                <code className="block flex-1 min-w-0 p-2 bg-white rounded font-mono text-xs break-all border border-amber-200">
                  {rotated}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(rotated, "Secret kopiert")}
                  data-testid="shopify-copy-rotated-btn"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Trage diesen Wert jetzt in Shopify → Settings → Notifications →
                Webhooks → "Webhook signing secret" ein. Sobald du diese Seite
                verlässt, ist der Secret nicht mehr abrufbar.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Setup-Anleitung */}
      <Card className="p-6 border border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Setup in Shopify</h2>
        </div>
        <ol className="space-y-4 text-sm text-slate-700">
          <li>
            <span className="font-medium">1. Webhook in Shopify einrichten:</span>
            <p className="mt-1 text-slate-600">
              Shopify-Admin → <em>Settings</em> → <em>Notifications</em> → Section "Webhooks" → Button "Create webhook".
            </p>
          </li>
          <li>
            <span className="font-medium">2. URL und Format:</span>
            <div className="mt-2 flex items-center gap-2">
              <code className="block flex-1 p-2 bg-white rounded font-mono text-xs break-all border border-slate-200">
                {webhookUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(webhookUrl, "URL kopiert")}
                data-testid="shopify-copy-url-btn"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Format: <code>JSON</code> · API-Version: <code>2024-01</code> oder neuer.
            </p>
          </li>
          <li>
            <span className="font-medium">3. Topics abonnieren:</span>
            <ul className="mt-1.5 ml-4 space-y-0.5 text-slate-600">
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">orders/create</code> — neue Bestellung → Draft-Rechnung</li>
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">orders/paid</code> — bezahlte Bestellung → Rechnung als bezahlt</li>
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">customers/create</code> — Kunde upserten</li>
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">customers/update</code> — Kunde aktualisieren</li>
            </ul>
          </li>
          <li>
            <span className="font-medium">4. Webhook-Signing-Secret:</span>
            <p className="mt-1 text-slate-600">
              Übernimm das Secret aus Shopify in das Feld oben — <strong>oder</strong> rotiere
              hier einen neuen Secret und trage ihn in Shopify ein.
            </p>
          </li>
          <li>
            <span className="font-medium">5. Testen:</span>
            <p className="mt-1 text-slate-600">
              Klicke in Shopify auf "Send test notification". Schau danach unter{" "}
              <Link to="/invoices" className="text-blue-600 hover:underline" data-testid="shopify-link-invoices">
                Rechnungen
              </Link>{" "}
              — die Test-Order erscheint mit Quelle "shopify".
            </p>
          </li>
        </ol>
        <a
          href="https://shopify.dev/docs/apps/build/webhooks/configuration"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
          data-testid="shopify-docs-link"
        >
          Shopify-Webhook-Docs öffnen <ExternalLink className="w-3 h-3" />
        </a>
      </Card>
    </div>
  );
}
